import React from 'react';
import WorldClassAudioPlayer from '@/components/audio/WorldClassAudioPlayer';

interface AudioPlayerExampleProps {
  moduleId?: string;
  sectionId?: string;
}

export const AudioPlayerExample: React.FC<AudioPlayerExampleProps> = ({
  moduleId = 'demo-module',
  sectionId = 'demo-section',
}) => {
  const exampleText = `Welcome to the world-class audio player experience. 
  
  This advanced audio system features intelligent text highlighting that follows along as the audio plays, creating a karaoke-style reading experience. Each word lights up in real-time, helping you follow the content effortlessly.
  
  The player automatically pauses when you switch tabs, ensuring you never miss important content. It saves your progress continuously, so you can resume exactly where you left off, even if you close the browser.
  
  Control playback with keyboard shortcuts: Press spacebar to play or pause, arrow keys to skip forward and backward, S to stop, and M to mute. The interface is fully accessible and designed with WCAG guidelines in mind.
  
  Adjust the playback speed from half speed to double speed using the slider below. The volume control gives you precise audio level management. All your preferences and progress are automatically saved to the database.
  
  The smooth auto-scroll feature keeps the currently playing text in view, so you never have to manually scroll while listening. It's intelligent enough to only scroll when needed, maintaining a comfortable reading position.
  
  This is truly a premium audio learning experience, built with modern web technologies and best practices for performance, accessibility, and user experience.`;

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            World-Class Audio Player Demo
          </h1>
          <p className="text-muted-foreground">
            Experience premium audio playback with real-time text highlighting, auto-scroll, and intelligent progress tracking.
          </p>
        </div>

        <WorldClassAudioPlayer
          text={exampleText}
          moduleId={moduleId}
          sectionId={sectionId}
          courseId={moduleId || 'demo-course'}
          title="Demo Audio Content"
          onComplete={() => {
            console.log('Audio playback completed!');
          }}
        />

        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="text-xl font-semibold mb-4">Features</h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span><strong>Real-time Text Highlighting:</strong> Words light up as they're spoken</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span><strong>Auto-scroll:</strong> Page automatically scrolls to keep current word in view</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span><strong>Tab Detection:</strong> Auto-pauses when you switch tabs</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span><strong>Resume Playback:</strong> Continues from where you left off</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span><strong>Progress Persistence:</strong> Saves to database every 5 seconds</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span><strong>Keyboard Shortcuts:</strong> Space, arrows, S, M for full control</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span><strong>Speed Control:</strong> 0.5x to 2x playback speed</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span><strong>Volume Control:</strong> Precise audio level management</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span><strong>Working Stop/Pause:</strong> Proper playback state management</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayerExample;
